import axios from 'axios';
import { ENDPOINTS } from '../constants/endpoints';
import { AuthService } from './auth.service';
import {
    ICreateExpFormRequest,
    ICreateExpRealizationRequest,
    IBookExpReceivableRequest,
    IBookingRateSuggestion,
    IExpForm,
    IExpRealization,
    IExpRealizationPreview,
    IPage,
    IUpdateExpFormRequest,
    IUpdateExpRealizationRequest,
    ExpRealizationStatus,
} from '../interfaces/commercial.interface';

const headers = () => AuthService.getAuthHeader();

export interface ExpFormListFilters {
    page?: number;
    size?: number;
    status?: ExpRealizationStatus | '';
    bank?: string;
    /** R-27/T6: server-side "Overdue only" filter — the list request carries `overdueOnly=true`
     * verbatim, never a client-side re-filter of a fetched page (brief's binding test). */
    overdueOnly?: boolean;
}

export const ExpFormService = {
    list: async (institutionId: string, filters: ExpFormListFilters = {}): Promise<IPage<IExpForm>> => {
        const { page = 0, size = 20, status, bank, overdueOnly } = filters;
        const params: Record<string, string | number | boolean> = { page, size };
        if (status) params.status = status;
        if (bank) params.bank = bank;
        if (overdueOnly) params.overdueOnly = true;
        const res = await axios.get(ENDPOINTS.EXP_FORM.LIST(institutionId), { headers: headers(), params });
        return res.data;
    },

    get: async (institutionId: string, expId: string): Promise<IExpForm> =>
        (await axios.get(ENDPOINTS.EXP_FORM.GET(institutionId, expId), { headers: headers() })).data,

    create: async (institutionId: string, request: ICreateExpFormRequest): Promise<IExpForm> =>
        (await axios.post(ENDPOINTS.EXP_FORM.CREATE(institutionId), request, { headers: headers() })).data,

    update: async (institutionId: string, expId: string, request: IUpdateExpFormRequest): Promise<IExpForm> =>
        (await axios.put(ENDPOINTS.EXP_FORM.UPDATE(institutionId, expId), request, { headers: headers() })).data,

    /** Books the export receivable for an EXP that has none (booking was skipped or failed). */
    bookReceivable: async (institutionId: string, expId: string, request: IBookExpReceivableRequest): Promise<IExpForm> =>
        (await axios.post(ENDPOINTS.EXP_FORM.BOOK_RECEIVABLE(institutionId, expId), request, { headers: headers() })).data,

    /** Default for the booking-rate prompt: the latest rate entered for the currency. */
    suggestBookingRate: async (institutionId: string, currency: string): Promise<IBookingRateSuggestion> =>
        (await axios.get(ENDPOINTS.EXP_FORM.BOOKING_RATE_SUGGESTION(institutionId, currency), { headers: headers() })).data,

    remove: async (institutionId: string, expId: string): Promise<void> => {
        await axios.delete(ENDPOINTS.EXP_FORM.DELETE(institutionId, expId), { headers: headers() });
    },

    listRealizations: async (institutionId: string, expId: string, page = 0, size = 100): Promise<IPage<IExpRealization>> => {
        const res = await axios.get(ENDPOINTS.EXP_FORM.REALIZATIONS(institutionId, expId), { headers: headers(), params: { page, size } });
        return res.data;
    },

    createRealization: async (institutionId: string, expId: string, request: ICreateExpRealizationRequest): Promise<IExpRealization> =>
        (await axios.post(ENDPOINTS.EXP_FORM.REALIZATIONS(institutionId, expId), request, { headers: headers() })).data,

    /** W3-B2: bank-advice view and journal preview of a distributed realization (no writes). */
    previewRealization: async (institutionId: string, expId: string, request: ICreateExpRealizationRequest): Promise<IExpRealizationPreview> =>
        (await axios.post(ENDPOINTS.EXP_FORM.REALIZATION_PREVIEW(institutionId, expId), request, { headers: headers() })).data,

    updateRealization: async (institutionId: string, expId: string, realId: string, request: IUpdateExpRealizationRequest): Promise<IExpRealization> =>
        (await axios.put(ENDPOINTS.EXP_FORM.REALIZATION(institutionId, expId, realId), request, { headers: headers() })).data,

    /** Retry the ledger entry of a realization whose posting failed (booked-receivable EXPs only). */
    postRealizationToLedger: async (institutionId: string, expId: string, realId: string): Promise<IExpRealization> =>
        (await axios.post(ENDPOINTS.EXP_FORM.REALIZATION_POST_LEDGER(institutionId, expId, realId), null, { headers: headers() })).data,

    deleteRealization: async (institutionId: string, expId: string, realId: string): Promise<void> => {
        await axios.delete(ENDPOINTS.EXP_FORM.REALIZATION(institutionId, expId, realId), { headers: headers() });
    },
};
